import cache from './cache.js';
import VoxelStatistics from '../enums/VoxelStatistics.js';
import VoxelManager from '../utilities/VoxelManager.js';
import { createAndCacheLocalImage } from '../loaders/imageLoader.js';
import { getConstructorFromType } from '../utilities/getBufferConfiguration.js';
import { coreLog } from '../utilities/logger.js';
const log = coreLog.getLogger('cache', 'reducedVolumeImages');
export const REDUCED_IMAGE_SCHEME = 'reduced';
const REDUCED_IMAGE_ID = /^reduced:(\d+)x(\d+)(?:x(\d+))?(?:@(\d+),(\d+))?:([^:]+):(.+)$/;
const STORABLE_TYPES = new Set([
    'Uint8Array',
    'Uint16Array',
    'Int16Array',
    'Float32Array',
]);
export function reducedImageId({ sourceImageId, columns, rows, frames = 1, inPlaneOffset = [0, 0], statistic = VoxelStatistics.Average, }) {
    const [offsetI, offsetJ] = inPlaneOffset;
    const size = (frames > 1 ? `${columns}x${rows}x${frames}` : `${columns}x${rows}`) +
        (offsetI || offsetJ ? `@${offsetI},${offsetJ}` : '');
    return `${REDUCED_IMAGE_SCHEME}:${size}:${statistic}:${sourceImageId}`;
}
export function isReducedImageId(imageId) {
    return REDUCED_IMAGE_ID.test(imageId ?? '');
}
export function parseReducedImageId(imageId) {
    const match = REDUCED_IMAGE_ID.exec(imageId ?? '');
    if (!match) {
        return undefined;
    }
    return {
        columns: Number(match[1]),
        rows: Number(match[2]),
        frames: match[3] ? Number(match[3]) : 1,
        inPlaneOffset: [Number(match[4] ?? 0), Number(match[5] ?? 0)],
        statistic: match[6],
        sourceImageId: match[7],
    };
}
export function provideReducedImages({ grid, sourceImageIds, factors, sourceOffset = [0, 0, 0], statistic = VoxelStatistics.Average, dataType, numberOfComponents = 1, sharedCacheKey, frameOfReferenceUID, id, }) {
    const [columns, rows, depth] = grid.dimensions;
    const frameFactor = Math.max(Math.round(factors[2]), 1);
    if (!STORABLE_TYPES.has(dataType)) {
        log.warn(`the image cache holds no image of ${dataType}, so the reduced representation keeps an array of its own`);
        return undefined;
    }
    if (!sourceImageIds?.length) {
        return undefined;
    }
    const imageIds = [];
    for (let slice = 0; slice < depth; slice++) {
        const firstFrame = sourceOffset[2] + slice * frameFactor;
        if (firstFrame >= sourceImageIds.length) {
            log.warn(`the reduced grid of ${depth} slices asks for the source frame ${firstFrame}, and the source holds ${sourceImageIds.length} frames`);
            return undefined;
        }
        const frames = Math.min(frameFactor, sourceImageIds.length - firstFrame);
        const imageId = reducedImageId({
            sourceImageId: sourceImageIds[firstFrame],
            columns,
            rows,
            frames,
            inPlaneOffset: [sourceOffset[0], sourceOffset[1]],
            statistic,
        });
        if (!cache.getImage(imageId)) {
            const image = createReducedImage({
                imageId,
                grid,
                slice,
                dataType,
                numberOfComponents,
                sharedCacheKey,
                frameOfReferenceUID,
                referencedImageId: sourceImageIds[firstFrame],
            });
            if (!image) {
                return undefined;
            }
        }
        else if (sharedCacheKey) {
            cache.setImageSharedCacheKey(imageId, sharedCacheKey);
        }
        imageIds.push(imageId);
    }
    return {
        imageIds,
        voxelManager: VoxelManager.createImageVolumeVoxelManager({
            dimensions: grid.dimensions,
            imageIds,
            numberOfComponents,
            id,
            dataType,
        }),
    };
}
function createReducedImage({ imageId, grid, slice, dataType, numberOfComponents, sharedCacheKey, frameOfReferenceUID, referencedImageId, }) {
    const [columns, rows] = grid.dimensions;
    const Constructor = getConstructorFromType(dataType, false);
    try {
        return createAndCacheLocalImage(imageId, {
            scalarData: new Constructor(columns * rows * numberOfComponents),
            dimensions: [columns, rows],
            spacing: [grid.spacing[0], grid.spacing[1]],
            origin: originOfSlice(grid, slice),
            direction: Array.from(grid.direction),
            frameOfReferenceUID,
            referencedImageId,
            targetBuffer: { type: dataType },
            onCacheAdd: (image) => {
                image.sharedCacheKey = sharedCacheKey;
            },
        });
    }
    catch (error) {
        log.warn(`the image cache refused the reduced slice ${imageId}`, error);
        return undefined;
    }
}
function originOfSlice(grid, slice) {
    const distance = grid.spacing[2] * slice;
    return [
        grid.origin[0] + grid.direction[6] * distance,
        grid.origin[1] + grid.direction[7] * distance,
        grid.origin[2] + grid.direction[8] * distance,
    ];
}
